/**
 * State Template States and Transitions Editor
 * 
 * Allows editing states and transitions for a state template
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Checkbox,
  Button,
  Space,
  Tag,
  Table,
  message,
  Tabs,
  Tooltip
} from 'antd';
import type { SortOrder } from 'antd/es/table/interface';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { apiClient } from '../../services/apiClient';
import OptionCatalogManager from '../PrimeModel/OptionCatalogManager';
import { PropertyChangesEditor } from '../PrimeModel/PropertyChangesEditor';
import { ValidationRulesEditor } from '../Parameters/ValidationRulesEditor';
import type { TransitionParameters, ValidationRules, PropertyChange } from '../../types/transitionParameters';

const { Option } = Select;
const { TextArea } = Input;

interface StateTemplateState {
  id?: string;
  template_id?: string;
  name: string;
  description?: string;
  is_initial: boolean;
  is_final: boolean;
  is_improvement: boolean;
  is_resource_active: boolean;
  state_type: string;
  sequence_order: number;
  default_parameters?: Record<string, any>;
}

interface StateTemplateTransition {
  id?: string;
  template_id?: string;
  name: string;
  description?: string;
  from_state: string | null;
  to_state: string;
  default_transition_type: string;
  is_secondary: boolean;
  is_improvement: boolean;
  sequence_order: number;
  default_parameters?: Record<string, any>;
  validation_rules?: Record<string, any>;
}

interface StateTemplatePropertyMapping {
  id?: string;
  template_id?: string;
  property_key: string;
  property_type: string;
  global_option_catalog_ref?: string;
  created_in_states?: string[];
  updated_in_states?: string[];
  referenced_artifact_type_ids?: string[];
  is_improvement?: boolean;
}

interface StateTemplateStatesTransitionsEditorProps {
  template: {
    id: string;
    name: string;
    states: StateTemplateState[];
    transitions: StateTemplateTransition[];
    property_mappings?: StateTemplatePropertyMapping[];
  };
  visible: boolean;
  onClose: () => void;
  onSave: (updatedTemplate?: any) => void | Promise<void>;
}

interface OptionCatalog {
  id: string;
  name: string;
  display_label?: string;
}

interface ArtifactType {
  id: string;
  name: string;
}

export const StateTemplateStatesTransitionsEditor: React.FC<StateTemplateStatesTransitionsEditorProps> = ({
  template,
  visible,
  onClose,
  onSave
}) => {
  const [states, setStates] = useState<StateTemplateState[]>([]);
  const [transitions, setTransitions] = useState<StateTemplateTransition[]>([]);
  const [propertyMappings, setPropertyMappings] = useState<StateTemplatePropertyMapping[]>([]);
  const [showStateModal, setShowStateModal] = useState(false);
  const [editingState, setEditingState] = useState<StateTemplateState | null>(null);
  const [showTransitionModal, setShowTransitionModal] = useState(false);
  const [editingTransition, setEditingTransition] = useState<StateTemplateTransition | null>(null);
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [editingProperty, setEditingProperty] = useState<StateTemplatePropertyMapping | null>(null);
  const [showCatalogManager, setShowCatalogManager] = useState(false);
  const [stateForm] = Form.useForm();
  const [transitionForm] = Form.useForm();
  const [propertyForm] = Form.useForm();
  const initializedRef = useRef<string | null>(null);
  
  // Property operations and validation rules state for transition editing
  const [transitionPropertyChanges, setTransitionPropertyChanges] = useState<PropertyChange[]>([]);
  const [transitionValidationRules, setTransitionValidationRules] = useState<ValidationRules>({});
  const [transitionModalActiveTab, setTransitionModalActiveTab] = useState<string>('basic');
  
  // Filter states
  const [stateFilter, setStateFilter] = useState<'all' | 'improvement' | 'operational'>('all');
  const [transitionFilter, setTransitionFilter] = useState<'all' | 'improvement' | 'operational'>('all');
  const [propertyFilter, setPropertyFilter] = useState<'all' | 'improvement' | 'operational'>('all');
  
  // Option catalogs and artifact types for properties
  const [optionCatalogs, setOptionCatalogs] = useState<OptionCatalog[]>([]);
  const [artifactTypes, setArtifactTypes] = useState<ArtifactType[]>([]);

  // Fetch option catalogs and artifact types
  const fetchOptionCatalogs = useCallback(async () => {
    try {
      const response = await apiClient.get('/option-catalogs');
      setOptionCatalogs(response.data || []);
    } catch (error) {
      console.error('Error fetching option catalogs:', error);
      setOptionCatalogs([]);
    }
  }, []);

  const fetchArtifactTypes = useCallback(async () => {
    try {
      const response = await apiClient.get('/artifact-types');
      const types = Array.isArray(response.data) ? response.data : (response.data?.items || []);
      setArtifactTypes(types);
    } catch (error) {
      console.error('Error fetching artifact types:', error);
      setArtifactTypes([]);
    }
  }, []);

  useEffect(() => {
    if (visible) {
      fetchOptionCatalogs();
      fetchArtifactTypes();
    }
  }, [visible, fetchOptionCatalogs, fetchArtifactTypes]);

  // Helper function to deduplicate states/transitions by name
  const deduplicateStates = (statesArray: StateTemplateState[]): StateTemplateState[] => {
    const seen = new Set<string>();
    return statesArray.filter(state => {
      if (seen.has(state.name)) {
        console.warn(`Duplicate state found: ${state.name}`);
        return false;
      }
      seen.add(state.name);
      return true;
    });
  };

  const deduplicateTransitions = (transitionsArray: StateTemplateTransition[]): StateTemplateTransition[] => {
    const seen = new Set<string>();
    return transitionsArray.filter(transition => {
      if (seen.has(transition.name)) {
        console.warn(`Duplicate transition found: ${transition.name}`);
        return false;
      }
      seen.add(transition.name);
      return true;
    });
  };

  useEffect(() => {
    if (visible && template) {
      const templateId = template.id;
      const statesCount = (template.states || []).length;
      const transitionsCount = (template.transitions || []).length;
      const statesKey = `${templateId}-${statesCount}-${transitionsCount}`;
      
      // Initialize when modal opens, template changes, or states/transitions count changes
      if (initializedRef.current !== statesKey) {
        console.log('Initializing states/transitions for template:', templateId, 'states:', statesCount, 'transitions:', transitionsCount, 'current key:', initializedRef.current, 'new key:', statesKey);
        const deduplicatedStates = deduplicateStates(template.states || []);
        const deduplicatedTransitions = deduplicateTransitions(template.transitions || []);
        console.log('Deduplicated states:', deduplicatedStates.length, 'from', statesCount);
        setStates(deduplicatedStates);
        setTransitions(deduplicatedTransitions);
        setPropertyMappings(template.property_mappings || []);
        initializedRef.current = statesKey;
      } else {
        console.log('Skipping re-initialization - already initialized with key:', statesKey);
      }
    } else if (!visible) {
      // Reset when modal closes
      initializedRef.current = null;
      setStates([]);
      setTransitions([]);
      setPropertyMappings([]);
      setEditingState(null);
      setEditingTransition(null);
      setEditingProperty(null);
      setStateFilter('all');
      setTransitionFilter('all');
      setPropertyFilter('all');
    }
  }, [visible, template?.id, template?.states?.length, template?.transitions?.length]);

  // Get max sequence order
  const getMaxSequence = (items: Array<{ sequence_order: number }>) => {
    if (items.length === 0) return 0;
    return Math.max(...items.map(item => item.sequence_order || 0), 0);
  };

  // Get state by name
  const getStateByName = (name: string | null): StateTemplateState | null => {
    if (!name) return null;
    return states.find(s => s.name === name) || null;
  };

  // Validate and auto-correct transition improvement consistency
  // Improvement flag should match to_state - if it doesn't, we auto-correct it rather than blocking
  const validateTransitionImprovement = (fromStateName: string | null, toStateName: string, isImprovement: boolean): { error: string | null, correctedValue: boolean } => {
    const fromState = getStateByName(fromStateName);
    const toState = getStateByName(toStateName);
    
    // Always use to_state's improvement status as the source of truth
    const correctImprovementValue = toState ? toState.is_improvement : false;
    
    if (fromState && toState) {
      // Warn but don't block if states cross improvement/operational boundary
      // We allow this but auto-correct the transition flag to match to_state
      if (fromState.is_improvement !== toState.is_improvement) {
        // This is allowed - transitions can cross boundaries, but we'll use to_state as the reference
      }
    }
    
    // Auto-correct if the improvement flag doesn't match to_state
    if (toState && isImprovement !== correctImprovementValue) {
      return {
        error: null, // Don't block, just auto-correct
        correctedValue: correctImprovementValue
      };
    }
    
    return { error: null, correctedValue: isImprovement };
  };

  // Auto-set transition improvement from states
  const autoSetTransitionImprovement = (fromStateName: string | null, toStateName: string): boolean => {
    const toState = getStateByName(toStateName);
    if (toState) {
      return toState.is_improvement;
    }
    return false;
  };

  const handleAddState = () => {
    setEditingState(null);
    const maxSeq = getMaxSequence(states);
    stateForm.resetFields();
    stateForm.setFieldsValue({
      sequence_order: maxSeq + 1,
      state_type: 'OPERATIONAL',
      is_initial: false,
      is_final: false,
      is_improvement: false,
      is_resource_active: false
    });
    setShowStateModal(true);
  };

  const handleEditState = (state: StateTemplateState) => {
    setEditingState(state);
    stateForm.setFieldsValue(state);
    setShowStateModal(true);
  };

  const handleDeleteState = (stateName: string) => {
    setStates(prevStates => prevStates.filter(s => s.name !== stateName));
    setTransitions(prevTransitions => prevTransitions.filter(t => t.from_state !== stateName && t.to_state !== stateName));
  };

  const handleMoveStateSequence = (stateName: string, direction: 'up' | 'down') => {
    setStates(prevStates => {
      const sorted = [...prevStates].sort((a, b) => (a.sequence_order || 0) - (b.sequence_order || 0));
      const index = sorted.findIndex(s => s.name === stateName);
      if (index === -1) return prevStates;
      
      if (direction === 'up' && index > 0) {
        const temp = sorted[index].sequence_order;
        sorted[index].sequence_order = sorted[index - 1].sequence_order;
        sorted[index - 1].sequence_order = temp;
      } else if (direction === 'down' && index < sorted.length - 1) {
        const temp = sorted[index].sequence_order;
        sorted[index].sequence_order = sorted[index + 1].sequence_order;
        sorted[index + 1].sequence_order = temp;
      }
      
      return sorted;
    });
  };

  const handleSaveState = async () => {
    try {
      const values = await stateForm.validateFields();
      const seqOrder = typeof values.sequence_order === 'string' ? parseInt(values.sequence_order) || 0 : values.sequence_order || 0;
      const stateData: StateTemplateState = {
        ...values,
        sequence_order: seqOrder
      };

      if (editingState) {
        setStates(prevStates => {
          const updated = prevStates.map(s => s.name === editingState.name ? stateData : s);
          return updated;
        });
      } else {
        setStates(prevStates => {
          if (prevStates.some(s => s.name === stateData.name)) {
            message.error(`A state with the name "${stateData.name}" already exists`);
            return prevStates;
          }
          const updated = [...prevStates, stateData];
          return updated;
        });
      }

      setShowStateModal(false);
      setEditingState(null);
      stateForm.resetFields();
    } catch (error) {
      console.error('Error saving state:', error);
    }
  };

  const handleAddTransition = () => {
    setEditingTransition(null);
    const maxSeq = getMaxSequence(transitions);
    transitionForm.resetFields();
    transitionForm.setFieldsValue({
      sequence_order: maxSeq + 1,
      default_transition_type: 'PROCESSITEM',
      is_secondary: false,
      is_improvement: false,
      from_state: null,
      to_state: undefined
    });
    setTransitionPropertyChanges([]);
    setTransitionValidationRules({});
    setTransitionModalActiveTab('basic');
    setShowTransitionModal(true);
  };

  const handleEditTransition = (transition: StateTemplateTransition) => {
    setEditingTransition(transition);
    transitionForm.setFieldsValue(transition);
    
    // Parse default_parameters and validation_rules
    let propertyChanges: PropertyChange[] = [];
    let validationRules: ValidationRules = {};
    
    if (transition.default_parameters) {
      const params = transition.default_parameters as TransitionParameters;
      propertyChanges = params.property_changes || [];
    }
    
    if (transition.validation_rules) {
      validationRules = transition.validation_rules as ValidationRules;
    }
    
    setTransitionPropertyChanges(propertyChanges);
    setTransitionValidationRules(validationRules);
    setTransitionModalActiveTab('basic');
    setShowTransitionModal(true);
  };

  const handleDeleteTransition = (transitionName: string) => {
    setTransitions(prevTransitions => prevTransitions.filter(t => t.name !== transitionName));
  };

  const handleMoveTransitionSequence = (transitionName: string, direction: 'up' | 'down') => {
    setTransitions(prevTransitions => {
      const sorted = [...prevTransitions].sort((a, b) => (a.sequence_order || 0) - (b.sequence_order || 0));
      const index = sorted.findIndex(t => t.name === transitionName);
      if (index === -1) return prevTransitions;
      
      if (direction === 'up' && index > 0) {
        const temp = sorted[index].sequence_order;
        sorted[index].sequence_order = sorted[index - 1].sequence_order;
        sorted[index - 1].sequence_order = temp;
      } else if (direction === 'down' && index < sorted.length - 1) {
        const temp = sorted[index].sequence_order;
        sorted[index].sequence_order = sorted[index + 1].sequence_order;
        sorted[index + 1].sequence_order = temp;
      }
      
      return sorted;
    });
  };

  const handleSaveTransition = async () => {
    try {
      const values = await transitionForm.validateFields();
      const seqOrder = typeof values.sequence_order === 'string' ? parseInt(values.sequence_order) || 0 : values.sequence_order || 0;
      const isImprovement = values.is_improvement || false;
      
      // Validate and auto-correct improvement consistency
      const validationResult = validateTransitionImprovement(values.from_state, values.to_state, isImprovement);
      if (validationResult.error) {
        message.error(validationResult.error);
        return;
      }
      // Use corrected value - it will auto-match the to_state's improvement status
      const correctedImprovement = validationResult.correctedValue;

      // Build default_parameters with property_changes
      const defaultParameters: TransitionParameters = {
        property_changes: transitionPropertyChanges.length > 0 ? transitionPropertyChanges : undefined
      };

      const transitionData: StateTemplateTransition = {
        ...values,
        sequence_order: seqOrder,
        is_improvement: correctedImprovement,
        default_parameters: Object.keys(defaultParameters).length > 0 && defaultParameters.property_changes ? defaultParameters : undefined,
        validation_rules: Object.keys(transitionValidationRules).length > 0 ? transitionValidationRules : undefined
      };

      if (editingTransition) {
        setTransitions(prevTransitions => {
          const updated = prevTransitions.map(t => t.name === editingTransition.name ? transitionData : t);
          return updated;
        });
      } else {
        setTransitions(prevTransitions => {
          if (prevTransitions.some(t => t.name === transitionData.name)) {
            message.error(`A transition with the name "${transitionData.name}" already exists`);
            return prevTransitions;
          }
          const updated = [...prevTransitions, transitionData];
          return updated;
        });
      }

      setShowTransitionModal(false);
      setEditingTransition(null);
      setTransitionPropertyChanges([]);
      setTransitionValidationRules({});
      transitionForm.resetFields();
    } catch (error) {
      console.error('Error saving transition:', error);
    }
  };

  // Property management handlers
  const handleAddProperty = () => {
    setEditingProperty(null);
    propertyForm.resetFields();
    propertyForm.setFieldsValue({
      property_type: 'string',
      created_in_states: [],
      updated_in_states: [],
      referenced_artifact_type_ids: [],
      is_improvement: false
    });
    setShowPropertyModal(true);
  };

  const handleEditProperty = (property: StateTemplatePropertyMapping) => {
    setEditingProperty(property);
    propertyForm.setFieldsValue({
      property_key: property.property_key,
      property_type: property.property_type,
      global_option_catalog_ref: property.global_option_catalog_ref,
      created_in_states: property.created_in_states || [],
      updated_in_states: property.updated_in_states || [],
      referenced_artifact_type_ids: property.referenced_artifact_type_ids || [],
      is_improvement: property.is_improvement || false
    });
    setShowPropertyModal(true);
  };

  const handleDeleteProperty = async (propertyId: string | undefined, propertyKey: string) => {
    if (propertyId) {
      try {
        await apiClient.delete(`/template-property-mappings/${propertyId}`);
        message.success('Property mapping deleted');
        setPropertyMappings(prev => prev.filter(p => p.id !== propertyId));
      } catch (error: any) {
        console.error('Error deleting property mapping:', error);
        message.error('Failed to delete property mapping: ' + (error.response?.data?.detail || error.message));
      }
    } else {
      // Local only, not yet saved
      setPropertyMappings(prev => prev.filter(p => p.property_key !== propertyKey));
    }
  };

  const handleSaveProperty = async () => {
    try {
      const values = await propertyForm.validateFields();
      
      // is_improvement is purely informational for properties - no validation required
      // User's manual checkbox selection takes precedence
      let isImprovement = values.is_improvement || false;
      
      // If user hasn't explicitly checked it, auto-infer from created_in_states if all states are improvement
      if (!values.is_improvement && values.created_in_states && Array.isArray(values.created_in_states) && values.created_in_states.length > 0) {
        const allImprovement = values.created_in_states.every((stateName: string) => {
          const state = states.find(s => s.name === stateName);
          return state?.is_improvement === true;
        });
        if (allImprovement && values.created_in_states.length > 0) {
          isImprovement = true;
        }
      }

      // Prepare property data for backend - include is_improvement
      const propertyDataForBackend: any = {
        property_key: values.property_key,
        property_type: values.property_type,
        is_improvement: isImprovement
      };
      
      // Only include optional fields if they have values
      // Validate that global_option_catalog_ref is a valid UUID if provided
      if (values.global_option_catalog_ref) {
        // Check if it's a valid UUID format (basic validation)
        const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (uuidPattern.test(values.global_option_catalog_ref)) {
          propertyDataForBackend.global_option_catalog_ref = values.global_option_catalog_ref;
        } else {
          // If it's not a valid UUID, log a warning and don't include it
          console.warn(`Invalid global_option_catalog_ref value: "${values.global_option_catalog_ref}". Expected a UUID. Skipping.`);
        }
      }
      if (values.created_in_states && Array.isArray(values.created_in_states) && values.created_in_states.length > 0) {
        propertyDataForBackend.created_in_states = values.created_in_states;
      }
      if (values.updated_in_states && Array.isArray(values.updated_in_states) && values.updated_in_states.length > 0) {
        propertyDataForBackend.updated_in_states = values.updated_in_states;
      }
      if (values.referenced_artifact_type_ids && Array.isArray(values.referenced_artifact_type_ids) && values.referenced_artifact_type_ids.length > 0) {
        propertyDataForBackend.referenced_artifact_type_ids = values.referenced_artifact_type_ids;
      }

      // Use the same data for frontend state
      const propertyDataWithFrontend: StateTemplatePropertyMapping = {
        ...propertyDataForBackend
      };

      if (editingProperty && editingProperty.id) {
        try {
          const response = await apiClient.put(`/template-property-mappings/${editingProperty.id}`, propertyDataForBackend);
          message.success('Property mapping updated');
          // Use response data if available, otherwise use the data we sent
          const updatedProperty = response.data 
            ? { ...response.data, is_improvement: response.data.is_improvement ?? isImprovement }
            : { ...propertyDataWithFrontend, id: editingProperty.id, template_id: editingProperty.template_id };
          setPropertyMappings(prev => prev.map(p => 
            p.id === editingProperty.id ? updatedProperty : p
          ));
        } catch (error: any) {
          console.error('Error updating property mapping:', error);
          const errorDetail = error.response?.data?.detail || error.message;
          message.error('Failed to update property mapping: ' + errorDetail);
          console.error('Request payload:', propertyDataForBackend);
          return;
        }
      } else {
        try {
          const response = await apiClient.post(`/state-templates/${template.id}/property-mappings`, propertyDataForBackend);
          message.success('Property mapping created');
          // Use response data if available, otherwise use the data we sent
          const newProperty = response.data 
            ? { ...response.data, is_improvement: response.data.is_improvement ?? isImprovement }
            : { ...propertyDataWithFrontend, id: '', template_id: template.id };
          setPropertyMappings(prev => [...prev, newProperty]);
        } catch (error: any) {
          console.error('Error creating property mapping:', error);
          const errorDetail = error.response?.data?.detail || error.message;
          message.error('Failed to create property mapping: ' + errorDetail);
          console.error('Request payload:', propertyDataForBackend);
          return;
        }
      }

      setShowPropertyModal(false);
      setEditingProperty(null);
      propertyForm.resetFields();
    } catch (error) {
      console.error('Error saving property mapping:', error);
    }
  };

  const handleSaveAll = async () => {
    try {
      // Validate all transitions and auto-correct improvement flags
      for (const transition of transitions) {
        const validationResult = validateTransitionImprovement(transition.from_state, transition.to_state, transition.is_improvement);
        if (validationResult.error) {
          message.error(`Transition "${transition.name}": ${validationResult.error}`);
          return;
        }
        // Auto-correct the improvement flag if needed
        if (transition.is_improvement !== validationResult.correctedValue) {
          transition.is_improvement = validationResult.correctedValue;
        }
      }

      // Deduplicate states and transitions before saving
      const uniqueStates = deduplicateStates(states);
      const uniqueTransitions = deduplicateTransitions(transitions);

      // Clean states
      const cleanedStates = uniqueStates.map(state => ({
        name: state.name,
        description: state.description || undefined,
        is_initial: state.is_initial || false,
        is_final: state.is_final || false,
        is_improvement: state.is_improvement || false,
        state_type: state.state_type || 'OPERATIONAL',
        is_resource_active: state.is_resource_active || false,
        sequence_order: typeof state.sequence_order === 'string' ? parseInt(state.sequence_order) || 0 : state.sequence_order || 0,
        default_parameters: state.default_parameters || undefined
      }));

      // Clean transitions - include is_improvement
      const cleanedTransitions = uniqueTransitions.map(transition => ({
        name: transition.name,
        description: transition.description || undefined,
        from_state: transition.from_state || null,
        to_state: transition.to_state,
        default_transition_type: transition.default_transition_type || 'PROCESSITEM',
        is_secondary: transition.is_secondary || false,
        is_improvement: transition.is_improvement || false,
        sequence_order: typeof transition.sequence_order === 'string' ? parseInt(transition.sequence_order) || 0 : transition.sequence_order || 0,
        default_parameters: transition.default_parameters || undefined,
        validation_rules: transition.validation_rules || undefined
      }));

      console.log('Saving states:', cleanedStates.length, 'transitions:', cleanedTransitions.length);

      const response = await apiClient.put(`/state-templates/${template.id}`, {
        states: cleanedStates,
        transitions: cleanedTransitions
      });
      
      console.log('Save response:', response.data);
      
      // Update local state with saved data
      let savedStates: StateTemplateState[] = [];
      let savedTransitions: StateTemplateTransition[] = [];
      
      if (response.data) {
        const responseStates = (response.data.states || []).map((s: any) => ({
          id: s.id,
          template_id: s.template_id,
          name: s.name,
          description: s.description ?? undefined,
          is_initial: s.is_initial || false,
          is_final: s.is_final || false,
          is_improvement: s.is_improvement || false,
          is_resource_active: s.is_resource_active || false,
          state_type: s.state_type || 'OPERATIONAL',
          sequence_order: s.sequence_order || 0,
          default_parameters: s.default_parameters ?? undefined
        }));
        
        const responseTransitions = (response.data.transitions || []).map((t: any) => ({
          id: t.id,
          template_id: t.template_id,
          name: t.name,
          description: t.description ?? undefined,
          from_state: t.from_state,
          to_state: t.to_state,
          default_transition_type: t.default_transition_type || 'PROCESSITEM',
          is_secondary: t.is_secondary || false,
          is_improvement: t.is_improvement || false,
          sequence_order: t.sequence_order || 0,
          default_parameters: t.default_parameters ?? undefined,
          validation_rules: t.validation_rules ?? undefined
        }));
        
        savedStates = deduplicateStates(responseStates);
        savedTransitions = deduplicateTransitions(responseTransitions);
        setStates(savedStates);
        setTransitions(savedTransitions);
        
        const newStatesKey = `${template.id}-${savedStates.length}-${savedTransitions.length}`;
        initializedRef.current = newStatesKey;
      }
      
      message.success(`Saved ${savedStates.length} states and ${savedTransitions.length} transitions successfully`);
      await onSave(response.data);
      onClose();
    } catch (error: any) {
      console.error('Error saving template:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      message.error('Failed to save: ' + errorMessage);
    }
  };

  // Filtered data
  const filteredStates = states.filter(state => {
    if (stateFilter === 'improvement') return state.is_improvement;
    if (stateFilter === 'operational') return !state.is_improvement;
    return true;
  });

  const filteredTransitions = transitions.filter(transition => {
    if (transitionFilter === 'improvement') return transition.is_improvement;
    if (transitionFilter === 'operational') return !transition.is_improvement;
    return true;
  });

  const filteredProperties = propertyMappings.filter(property => {
    if (propertyFilter === 'improvement') return property.is_improvement;
    if (propertyFilter === 'operational') return !property.is_improvement;
    return true;
  });

  // Sorted states and transitions by sequence
  const sortedStates = [...filteredStates].sort((a, b) => (a.sequence_order || 0) - (b.sequence_order || 0));
  const sortedTransitions = [...filteredTransitions].sort((a, b) => (a.sequence_order || 0) - (b.sequence_order || 0));

  const stateColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: StateTemplateState, b: StateTemplateState) => a.name.localeCompare(b.name),
      sortDirections: ['ascend', 'descend'] as SortOrder[],
      render: (text: string, record: StateTemplateState) => (
        <Space>
          <strong>{text}</strong>
          {record.is_initial && <Tag color="green">Initial</Tag>}
          {record.is_final && <Tag color="red">Final</Tag>}
          {record.is_improvement && <Tag color="purple">Improvement</Tag>}
        </Space>
      )
    },
    {
      title: 'Type',
      dataIndex: 'state_type',
      key: 'state_type',
      sorter: (a: StateTemplateState, b: StateTemplateState) => a.state_type.localeCompare(b.state_type),
      sortDirections: ['ascend', 'descend'] as SortOrder[],
      render: (type: string) => <Tag>{type}</Tag>
    },
    {
      title: 'Sequence',
      dataIndex: 'sequence_order',
      key: 'sequence_order',
      sorter: (a: StateTemplateState, b: StateTemplateState) => (a.sequence_order || 0) - (b.sequence_order || 0),
      sortDirections: ['ascend', 'descend'] as SortOrder[],
      width: 100
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      sorter: (a: StateTemplateState, b: StateTemplateState) => (a.description || '').localeCompare(b.description || ''),
      sortDirections: ['ascend', 'descend'] as SortOrder[],
      ellipsis: true
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_: any, record: StateTemplateState, index: number) => {
        const sorted = [...states].sort((a, b) => (a.sequence_order || 0) - (b.sequence_order || 0));
        const sortedIndex = sorted.findIndex(s => s.name === record.name);
        const canMoveUp = sortedIndex > 0;
        const canMoveDown = sortedIndex < sorted.length - 1;
        
        return (
          <Space>
            <Tooltip title="Move up">
              <Button 
                size="small" 
                icon={<ArrowUpOutlined />} 
                onClick={() => handleMoveStateSequence(record.name, 'up')}
                disabled={!canMoveUp}
              />
            </Tooltip>
            <Tooltip title="Move down">
              <Button 
                size="small" 
                icon={<ArrowDownOutlined />} 
                onClick={() => handleMoveStateSequence(record.name, 'down')}
                disabled={!canMoveDown}
              />
            </Tooltip>
            <Button size="small" icon={<EditOutlined />} onClick={() => handleEditState(record)} />
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => {
                Modal.confirm({
                  title: 'Delete State',
                  icon: <ExclamationCircleOutlined />,
                  content: `Are you sure you want to delete "${record.name}"?`,
                  onOk: () => handleDeleteState(record.name)
                });
              }}
            />
          </Space>
        );
      }
    }
  ];

  const transitionColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: StateTemplateTransition, b: StateTemplateTransition) => a.name.localeCompare(b.name),
      sortDirections: ['ascend', 'descend'] as SortOrder[]
    },
    {
      title: 'From',
      dataIndex: 'from_state',
      key: 'from_state',
      sorter: (a: StateTemplateTransition, b: StateTemplateTransition) => (a.from_state || '').localeCompare(b.from_state || ''),
      sortDirections: ['ascend', 'descend'] as SortOrder[],
      render: (text: string | null) => text || <Tag>CREATION</Tag>
    },
    {
      title: 'To',
      dataIndex: 'to_state',
      key: 'to_state',
      sorter: (a: StateTemplateTransition, b: StateTemplateTransition) => a.to_state.localeCompare(b.to_state),
      sortDirections: ['ascend', 'descend'] as SortOrder[]
    },
    {
      title: 'Type',
      dataIndex: 'default_transition_type',
      key: 'default_transition_type',
      sorter: (a: StateTemplateTransition, b: StateTemplateTransition) => a.default_transition_type.localeCompare(b.default_transition_type),
      sortDirections: ['ascend', 'descend'] as SortOrder[],
      render: (type: string, record: StateTemplateTransition) => (
        <Space>
          <Tag>{type}</Tag>
          {record.is_improvement && <Tag color="purple">Improvement</Tag>}
        </Space>
      )
    },
    {
      title: 'Sequence',
      dataIndex: 'sequence_order',
      key: 'sequence_order',
      sorter: (a: StateTemplateTransition, b: StateTemplateTransition) => (a.sequence_order || 0) - (b.sequence_order || 0),
      sortDirections: ['ascend', 'descend'] as SortOrder[],
      width: 100
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_: any, record: StateTemplateTransition) => {
        const sorted = [...transitions].sort((a, b) => (a.sequence_order || 0) - (b.sequence_order || 0));
        const sortedIndex = sorted.findIndex(t => t.name === record.name);
        const canMoveUp = sortedIndex > 0;
        const canMoveDown = sortedIndex < sorted.length - 1;
        
        return (
          <Space>
            <Tooltip title="Move up">
              <Button 
                size="small" 
                icon={<ArrowUpOutlined />} 
                onClick={() => handleMoveTransitionSequence(record.name, 'up')}
                disabled={!canMoveUp}
              />
            </Tooltip>
            <Tooltip title="Move down">
              <Button 
                size="small" 
                icon={<ArrowDownOutlined />} 
                onClick={() => handleMoveTransitionSequence(record.name, 'down')}
                disabled={!canMoveDown}
              />
            </Tooltip>
            <Button size="small" icon={<EditOutlined />} onClick={() => handleEditTransition(record)} />
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => {
                Modal.confirm({
                  title: 'Delete Transition',
                  icon: <ExclamationCircleOutlined />,
                  content: `Are you sure you want to delete "${record.name}"?`,
                  onOk: () => handleDeleteTransition(record.name)
                });
              }}
            />
          </Space>
        );
      }
    }
  ];

  const propertyColumns = [
    {
      title: 'Property Key',
      dataIndex: 'property_key',
      key: 'property_key',
      sorter: (a: StateTemplatePropertyMapping, b: StateTemplatePropertyMapping) => a.property_key.localeCompare(b.property_key),
      sortDirections: ['ascend', 'descend'] as SortOrder[],
      render: (text: string) => <strong>{text}</strong>
    },
    {
      title: 'Type',
      dataIndex: 'property_type',
      key: 'property_type',
      sorter: (a: StateTemplatePropertyMapping, b: StateTemplatePropertyMapping) => a.property_type.localeCompare(b.property_type),
      sortDirections: ['ascend', 'descend'] as SortOrder[],
      render: (type: string) => <Tag color="blue">{type}</Tag>
    },
    {
      title: 'Improvement',
      dataIndex: 'is_improvement',
      key: 'is_improvement',
      sorter: (a: StateTemplatePropertyMapping, b: StateTemplatePropertyMapping) => {
        const aVal = a.is_improvement ? 1 : 0;
        const bVal = b.is_improvement ? 1 : 0;
        return aVal - bVal;
      },
      sortDirections: ['ascend', 'descend'] as SortOrder[],
      render: (isImprovement: boolean) => isImprovement ? <Tag color="purple">Improvement</Tag> : <Tag>Operational</Tag>
    },
    {
      title: 'Created In',
      dataIndex: 'created_in_states',
      key: 'created_in_states',
      render: (states: any) => {
        if (!states || !Array.isArray(states) || states.length === 0) return '-';
        return (
          <Space size={4} wrap>
            {states.slice(0, 3).map((name: string) => (
              <Tag key={name} color="green" style={{ fontSize: '11px' }}>{name}</Tag>
            ))}
            {states.length > 3 && <Tag style={{ fontSize: '11px' }}>+{states.length - 3}</Tag>}
          </Space>
        );
      }
    },
    {
      title: 'Updated In',
      dataIndex: 'updated_in_states',
      key: 'updated_in_states',
      render: (states: any) => {
        if (!states || !Array.isArray(states) || states.length === 0) return '-';
        return (
          <Space size={4} wrap>
            {states.slice(0, 3).map((name: string) => (
              <Tag key={name} color="orange" style={{ fontSize: '11px' }}>{name}</Tag>
            ))}
            {states.length > 3 && <Tag style={{ fontSize: '11px' }}>+{states.length - 3}</Tag>}
          </Space>
        );
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: StateTemplatePropertyMapping) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEditProperty(record)} />
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              Modal.confirm({
                title: 'Delete Property Mapping',
                icon: <ExclamationCircleOutlined />,
                content: `Are you sure you want to delete "${record.property_key}"?`,
                onOk: () => handleDeleteProperty(record.id, record.property_key)
              });
            }}
          />
        </Space>
      )
    }
  ];

  return (
    <>
      <Modal
        title={`Edit States & Transitions: ${template.name}`}
        open={visible}
        onCancel={onClose}
        onOk={handleSaveAll}
        width={1200}
        okText="Save All"
        cancelText="Cancel"
      >
        <Tabs 
          defaultActiveKey="states"
          items={[
            {
              key: 'states',
              label: `States (${filteredStates.length}/${states.length})`,
              children: (
                <>
                  <div style={{ marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAddState}>
                      Add State
                    </Button>
                    <Select
                      value={stateFilter}
                      onChange={setStateFilter}
                      style={{ width: 150 }}
                    >
                      <Option value="all">All States</Option>
                      <Option value="improvement">Improvement</Option>
                      <Option value="operational">Operational</Option>
                    </Select>
                  </div>
                  <Table
                    dataSource={sortedStates}
                    columns={stateColumns}
                    rowKey={(record) => record.id || record.name}
                    pagination={false}
                    size="small"
                  />
                </>
              )
            },
            {
              key: 'transitions',
              label: `Transitions (${filteredTransitions.length}/${transitions.length})`,
              children: (
                <>
                  <div style={{ marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAddTransition}>
                      Add Transition
                    </Button>
                    <Select
                      value={transitionFilter}
                      onChange={setTransitionFilter}
                      style={{ width: 150 }}
                    >
                      <Option value="all">All Transitions</Option>
                      <Option value="improvement">Improvement</Option>
                      <Option value="operational">Operational</Option>
                    </Select>
                  </div>
                  <Table
                    dataSource={sortedTransitions}
                    columns={transitionColumns}
                    rowKey={(record) => record.id || record.name}
                    pagination={false}
                    size="small"
                  />
                </>
              )
            },
            {
              key: 'properties',
              label: `Properties (${filteredProperties.length}/${propertyMappings.length})`,
              children: (
                <>
                  <div style={{ marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAddProperty}>
                      Add Property
                    </Button>
                    <Select
                      value={propertyFilter}
                      onChange={setPropertyFilter}
                      style={{ width: 150 }}
                    >
                      <Option value="all">All Properties</Option>
                      <Option value="improvement">Improvement</Option>
                      <Option value="operational">Operational</Option>
                    </Select>
                  </div>
                  {filteredProperties.length > 0 ? (
                    <Table
                      dataSource={filteredProperties}
                      columns={propertyColumns}
                      rowKey={(record) => record.id || record.property_key || `prop-${record.property_key}`}
                      pagination={false}
                      size="small"
                    />
                  ) : (
                    <p style={{ color: '#999', textAlign: 'center', padding: '20px' }}>
                      {propertyMappings.length === 0 
                        ? 'No property mappings defined. Click "Add Property" to add them.'
                        : 'No properties match the current filter.'}
                    </p>
                  )}
                </>
              )
            }
          ]}
        />
      </Modal>

      {/* State Modal */}
      <Modal
        title={editingState ? 'Edit State' : 'Add State'}
        open={showStateModal}
        onCancel={() => {
          setShowStateModal(false);
          stateForm.resetFields();
        }}
        onOk={handleSaveState}
        width={600}
      >
        <Form form={stateForm} layout="vertical">
          <Form.Item
            label="State Name"
            name="name"
            rules={[{ required: true, message: 'Please enter state name' }]}
          >
            <Input placeholder="e.g., Draft, Review, Approved" />
          </Form.Item>

          <Form.Item label="Description" name="description">
            <TextArea rows={2} placeholder="Describe this state" />
          </Form.Item>

          <Form.Item
            label="State Type"
            name="state_type"
            initialValue="OPERATIONAL"
            rules={[{ required: true }]}
          >
            <Select>
              <Option value="OPERATIONAL">Operational</Option>
              <Option value="MATERIAL">Material</Option>
              <Option value="RESOURCE">Resource</Option>
              <Option value="CREATION">Creation</Option>
              <Option value="IMPROVEMENT">Improvement</Option>
            </Select>
          </Form.Item>

          <Form.Item name="is_initial" valuePropName="checked" initialValue={false}>
            <Checkbox>Initial State</Checkbox>
          </Form.Item>

          <Form.Item name="is_final" valuePropName="checked" initialValue={false}>
            <Checkbox>Final State</Checkbox>
          </Form.Item>

          <Form.Item name="is_improvement" valuePropName="checked" initialValue={false}>
            <Checkbox>Improvement State</Checkbox>
          </Form.Item>

          <Form.Item name="is_resource_active" valuePropName="checked" initialValue={false}>
            <Checkbox>Resource Active (Available as Resource)</Checkbox>
          </Form.Item>

          <Form.Item label="Sequence Order" name="sequence_order" initialValue={0}>
            <Input type="number" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Transition Modal */}
      <Modal
        title={editingTransition ? 'Edit Transition' : 'Add Transition'}
        open={showTransitionModal}
        onCancel={() => {
          setShowTransitionModal(false);
          transitionForm.resetFields();
          setTransitionPropertyChanges([]);
          setTransitionValidationRules({});
        }}
        onOk={handleSaveTransition}
        width={1000}
      >
        <Tabs
          activeKey={transitionModalActiveTab}
          onChange={setTransitionModalActiveTab}
          items={[
            {
              key: 'basic',
              label: 'Basic Info',
              children: (
                <Form form={transitionForm} layout="vertical">
          <Form.Item
            label="Transition Name"
            name="name"
            rules={[{ required: true, message: 'Please enter transition name' }]}
          >
            <Input placeholder="e.g., Submit, Approve, Reject" />
          </Form.Item>

          <Form.Item label="Description" name="description">
            <TextArea rows={2} placeholder="Describe this transition" />
          </Form.Item>

          <Form.Item 
            label="From State" 
            name="from_state"
            tooltip="Leave empty for CREATION transitions"
          >
            <Select 
              placeholder="Leave empty for CREATION" 
              allowClear
              onChange={(value) => {
                const toState = transitionForm.getFieldValue('to_state');
                if (value && toState) {
                  const isImprovement = autoSetTransitionImprovement(value, toState);
                  transitionForm.setFieldsValue({ is_improvement: isImprovement });
                }
              }}
            >
              {states.map(state => (
                <Option key={state.name} value={state.name}>
                  {state.name} {state.is_improvement && <Tag color="purple" style={{ marginLeft: 4 }}>I</Tag>}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="To State"
            name="to_state"
            rules={[{ required: true, message: 'Please select target state' }]}
          >
            <Select 
              placeholder="Select target state"
              onChange={(value) => {
                const fromState = transitionForm.getFieldValue('from_state');
                if (value) {
                  const isImprovement = autoSetTransitionImprovement(fromState, value);
                  transitionForm.setFieldsValue({ is_improvement: isImprovement });
                }
              }}
            >
              {states.map(state => (
                <Option key={state.name} value={state.name}>
                  {state.name} {state.is_improvement && <Tag color="purple" style={{ marginLeft: 4 }}>I</Tag>}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Transition Type"
            name="default_transition_type"
            initialValue="PROCESSITEM"
            rules={[{ required: true }]}
          >
            <Select>
              <Option value="PROCESSITEM">Process Item</Option>
              <Option value="MATERIAL">Material</Option>
              <Option value="RESOURCE">Resource</Option>
              <Option value="CREATION">Creation</Option>
              <Option value="PROCESSITEM_PERSISTENT">Process Item (Persistent)</Option>
            </Select>
          </Form.Item>

          <Form.Item name="is_secondary" valuePropName="checked" initialValue={false}>
            <Checkbox>Secondary Transition (Optional/Backtracking)</Checkbox>
          </Form.Item>

          <Form.Item 
            name="is_improvement" 
            valuePropName="checked" 
            initialValue={false}
            tooltip="Auto-set based on states. Transitions cannot cross between improvement and operational states."
          >
            <Checkbox>Improvement Transition</Checkbox>
          </Form.Item>

          <Form.Item label="Sequence Order" name="sequence_order" initialValue={0}>
            <Input type="number" />
          </Form.Item>
                </Form>
              )
            },
            {
              key: 'property-operations',
              label: 'Property Operations',
              children: (
                <PropertyChangesEditor
                  propertyChanges={transitionPropertyChanges}
                  onChange={setTransitionPropertyChanges}
                  artifactProperties={propertyMappings.map(p => ({
                    property_key: p.property_key,
                    name: p.property_key,
                    property_type: p.property_type,
                    type: p.property_type,
                    description: undefined
                  }))}
                />
              )
            },
            {
              key: 'validation-rules',
              label: 'Validation Rules',
              children: (
                <ValidationRulesEditor
                  validationRules={transitionValidationRules}
                  onChange={setTransitionValidationRules}
                  artifactProperties={propertyMappings.map(p => ({
                    property_key: p.property_key,
                    name: p.property_key,
                    property_type: p.property_type,
                    type: p.property_type,
                    description: undefined
                  }))}
                  transitions={transitions.map(t => ({
                    name: t.name,
                    id: t.id,
                    description: t.description
                  }))}
                />
              )
            }
          ]}
        />
      </Modal>

      {/* Property Modal */}
      <Modal
        title={editingProperty ? 'Edit Property Mapping' : 'Add Property Mapping'}
        open={showPropertyModal}
        onCancel={() => {
          setShowPropertyModal(false);
          propertyForm.resetFields();
        }}
        onOk={handleSaveProperty}
        width={700}
      >
        <Form form={propertyForm} layout="vertical">
          <Form.Item
            label="Property Key"
            name="property_key"
            rules={[{ required: true, message: 'Please enter property key' }]}
          >
            <Input 
              placeholder="e.g., invoice_number, amount, status" 
              disabled={!!editingProperty?.id}
            />
          </Form.Item>

          <Form.Item
            label="Property Type"
            name="property_type"
            initialValue="string"
            rules={[{ required: true, message: 'Please select property type' }]}
          >
            <Select>
              <Option value="string">String</Option>
              <Option value="number">Number</Option>
              <Option value="numeric">Numeric</Option>
              <Option value="boolean">Boolean</Option>
              <Option value="date">Date</Option>
              <Option value="currency">Currency</Option>
              <Option value="duration">Duration</Option>
              <Option value="url">URL</Option>
              <Option value="uuid">UUID</Option>
              <Option value="uuid[]">UUID Array</Option>
              <Option value="list">List</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Global Option Catalog"
            name="global_option_catalog_ref"
            tooltip="Optional reference to an option catalog for enumerated values"
          >
            <Space.Compact style={{ width: '100%' }}>
              <Select
                placeholder="Select option catalog (optional)"
                allowClear
                showSearch
                optionFilterProp="children"
                style={{ flex: 1 }}
              >
                {optionCatalogs.map(catalog => (
                  <Option key={catalog.id} value={catalog.id}>
                    {catalog.display_label || catalog.name}
                  </Option>
                ))}
              </Select>
              <Button
                icon={<SettingOutlined />}
                onClick={() => setShowCatalogManager(true)}
                title="Manage Option Catalogs"
              />
            </Space.Compact>
          </Form.Item>

          <Form.Item
            label="Created In States"
            name="created_in_states"
            tooltip="States where this property is created"
            getValueFromEvent={(value) => value || []}
            normalize={(value) => Array.isArray(value) ? value : []}
          >
            <Select
              mode="multiple"
              placeholder="Select states where property is created"
              allowClear
            >
              {states.map(state => (
                <Option key={state.name} value={state.name}>
                  {state.name} {state.is_improvement && <Tag color="purple" style={{ marginLeft: 4 }}>I</Tag>}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Updated In States"
            name="updated_in_states"
            tooltip="States where this property can be updated"
            getValueFromEvent={(value) => value || []}
            normalize={(value) => Array.isArray(value) ? value : []}
          >
            <Select
              mode="multiple"
              placeholder="Select states where property can be updated"
              allowClear
            >
              {states.map(state => (
                <Option key={state.name} value={state.name}>
                  {state.name} {state.is_improvement && <Tag color="purple" style={{ marginLeft: 4 }}>I</Tag>}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Referenced Artifact Type IDs"
            name="referenced_artifact_type_ids"
            tooltip="Artifact types that reference this property"
            getValueFromEvent={(value) => value || []}
            normalize={(value) => Array.isArray(value) ? value : []}
          >
            <Select
              mode="multiple"
              placeholder="Select referenced artifact types (optional)"
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {artifactTypes.map(type => (
                <Option key={type.id} value={type.id}>
                  {type.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="is_improvement"
            valuePropName="checked"
            initialValue={false}
            tooltip="Mark as improvement property. Auto-inferred if all created_in_states are improvement states."
          >
            <Checkbox>Improvement Property</Checkbox>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Manage Option Catalogs"
        open={showCatalogManager}
        onCancel={() => setShowCatalogManager(false)}
        footer={null}
        width={1000}
      >
        <OptionCatalogManager
          visible={showCatalogManager}
          onClose={() => {
            setShowCatalogManager(false);
            // Refresh catalogs when catalog manager closes
            fetchOptionCatalogs();
          }}
          onCatalogSelected={(catalogId) => {
            // Refresh catalogs first, then set the value
            fetchOptionCatalogs().then(() => {
              propertyForm.setFieldsValue({ global_option_catalog_ref: catalogId });
              setShowCatalogManager(false);
            });
          }}
        />
      </Modal>
    </>
  );
};
